import { config } from '@/configs'

import { atomWithStorage } from 'jotai/utils'

import { FEATURE_FLAGS } from './featureFlags'

/** localStorage key holding the active feature flags, as a JSON array of flag keys. */
export const FEATURE_FLAGS_STORAGE_KEY = 'featureFlags'

const isKnownFlag = (value: unknown): value is string =>
  typeof value === 'string' && value in FEATURE_FLAGS

/**
 * Jotai atom storing the active feature flags as a Set of string keys, persisted in localStorage.
 *
 * We use `string` here to avoid TypeScript inference issues when `FEATURE_FLAGS` is empty.
 * The hook layer (`useFeatureFlag`) enforces type safety.
 *
 * `getOnInit` is required: without it the stored value is only read on mount, so a flag that
 * switches what a page renders would first show the flag-off version, then remount the subtree
 * with the flag-on one. It also means the reads below run at import time, outside any error
 * boundary and on the boot path of every environment — a malformed value must degrade to "no flag
 * active", never throw.
 *
 * Persistence is scoped to `testingTooling` environments: elsewhere the Testing Panel is not
 * mounted, so a value typed by hand into the console — or left over in a browser — must not
 * switch a feature on. UAT and production always serve the default.
 */
export const featureFlagsAtom = atomWithStorage<Set<string>>(
  FEATURE_FLAGS_STORAGE_KEY,
  new Set<string>(),
  {
    getItem: (key, initialValue) => {
      if (!config.testingTooling) return initialValue

      try {
        const raw = localStorage.getItem(key)
        if (!raw) return initialValue

        const parsed: unknown = JSON.parse(raw)
        return Array.isArray(parsed) ? new Set(parsed.filter(isKnownFlag)) : initialValue
      } catch {
        return initialValue
      }
    },
    setItem: (key, value) => {
      if (!config.testingTooling) return

      try {
        localStorage.setItem(key, JSON.stringify([...value]))
      } catch {
        // Storage unavailable (private mode, quota exceeded): keep the flags in memory only.
      }
    },
    removeItem: key => {
      if (!config.testingTooling) return

      try {
        localStorage.removeItem(key)
      } catch {
        // Same as above.
      }
    },
  },
  { getOnInit: true },
)
